import React, { Suspense, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Award, BarChart3, Building2, FileText, Flag, History, Images, Inbox, KeyRound, LogOut, Menu, Loader2, Palette, Rocket, Scale, ServerCog, UserX } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth-context";
import { useUsageTracking } from "../../lib/usage";
import { GlobalSearch } from "./global-search";
import { SideMenu, SideNav, isDesktop, useSideNavCollapsed, type NavItem } from "../nav/side-menu";
import { NotificationHost } from "../ui/notification-host";
import { useSignalementCount } from "../../lib/signalement-count";
import { usePdfRequestCount } from "../../lib/pdf-request-count";

import { useTranslation } from "react-i18next";

const NAV_ITEMS: NavItem[] = [
  { path: "/super-admin", labelKey: "superadmin:layout.nav.inbox", icon: Inbox },
  { path: "/super-admin/signalements", labelKey: "superadmin:layout.nav.signalements", icon: Flag },
  { path: "/super-admin/pdf-requests", labelKey: "superadmin:layout.nav.pdfRequests", icon: FileText },
  { path: "/super-admin/erasures", labelKey: "superadmin:layout.nav.erasures", icon: UserX },
  { path: "/super-admin/clubs", labelKey: "superadmin:layout.nav.clubs", icon: Building2 },
  { path: "/super-admin/media", labelKey: "superadmin:layout.nav.media", icon: Images },
  { path: "/super-admin/badges", labelKey: "superadmin:layout.nav.badges", icon: Award },
  { path: "/super-admin/usage", labelKey: "superadmin:layout.nav.usage", icon: BarChart3 },
  { path: "/super-admin/journal", labelKey: "superadmin:layout.nav.journal", icon: History },
  { path: "/super-admin/queue", labelKey: "superadmin:layout.nav.queue", icon: ServerCog },
  { path: "/super-admin/setup-tokens", labelKey: "superadmin:layout.nav.setupTokens", icon: KeyRound },
  { path: "/super-admin/appearance", labelKey: "superadmin:layout.nav.appearance", icon: Palette },
  { path: "/super-admin/site-host", labelKey: "superadmin:layout.nav.siteHost", icon: Scale },
  { path: "/super-admin/site-readiness", labelKey: "superadmin:layout.nav.siteReadiness", icon: Rocket },
];

const SIGNALEMENTS_PATH = "/super-admin/signalements";
const PDF_REQUESTS_PATH = "/super-admin/pdf-requests";

/**
 * Frame of the platform-operator screens: three destinations named after what
 * the operator does (not after tables), and a search that is always there.
 */
export function SuperAdminLayout() {
  const { t } = useTranslation("superadmin");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSideNavCollapsed();
  // Reports waiting: shown on the entry itself, so a quiet queue stays quiet.
  const signalements = useSignalementCount();
  const pdfRequests = usePdfRequestCount();
  const navItems = useMemo(
    () => NAV_ITEMS.map((item) => {
      const badge = item.path === SIGNALEMENTS_PATH ? signalements : item.path === PDF_REQUESTS_PATH ? pdfRequests : null;

      return badge === null ? item : { ...item, badge };
    }),
    [signalements, pdfRequests],
  );

  // The operator's own screens count as activity too.
  useUsageTracking();

  const menuProps = {
    items: navItems,
    activePath: location.pathname,
    onNavigate: navigate,
    userName: user?.name ?? "",
    roleLabel: t("layout.administration"),
    onLogout: logout,
  };

  return (
    <>
      <div className="min-h-screen bg-background">
        <a href="#contenu" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-card focus:px-3 focus:py-2">
          {t("layout.allerAuContenu")}
        </a>
        <header className="sticky top-0 z-30 border-b border-border bg-card/95 pt-[env(safe-area-inset-top)] backdrop-blur">
          <div className="flex h-16 items-center gap-2 px-4 sm:h-20 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => (isDesktop() ? toggleCollapsed() : setMenuOpen(true))}
              className="shrink-0"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <button type="button" onClick={() => navigate("/super-admin")} className="flex shrink-0 items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground" aria-hidden>
                柔
              </span>
              <span className="hidden text-base font-bold leading-tight sm:inline">{t("layout.administration")}</span>
            </button>

            <div className="min-w-0 flex-1 md:mx-auto md:max-w-md">
              <GlobalSearch />
            </div>

            <span className="hidden text-sm text-muted-foreground lg:inline">{user?.name}</span>
            <Button variant="ghost" size="icon" onClick={logout} className="shrink-0 md:hidden" aria-label={t("layout.seDeconnecter")} title={t("layout.seDeconnecter")}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <NotificationHost />

        <div className="md:flex">
          <SideNav {...menuProps} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
          <main id="contenu" className="mx-auto min-w-0 max-w-5xl flex-1 px-4 py-6 pb-[calc(2rem+env(safe-area-inset-bottom))] md:py-8">
            {/* Pages are lazy: only this block waits, the operator's frame stays up. */}
            <Suspense fallback={<div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>

      <SideMenu {...menuProps} open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
